import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { Profile } from '@modules/auth/model';
import { constVoid, Lazy, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as T from 'fp-ts/Task';

import * as AuthService from './service';
import { sequenceS } from 'fp-ts/Apply';
import { DebouncedLineLoader } from '@layout/loaders/line-loader/LineLoader';

const AUTH_LOGOUT_KEY = 'PG_LOGOUT';

export interface AuthContextValue {
  profile: O.Option<Profile>;
  updateProfile: (profile: Profile) => void;
  requestUpdateProfile: Lazy<void>;
  logout: T.Task<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  profile: O.none,
  updateProfile: constVoid,
  requestUpdateProfile: constVoid,
  logout: T.never,
});

export const AuthContextProvider: FC = ({ children }) => {
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(true);

  const [profile, setProfile] = useState<O.Option<Profile>>(O.none);

  const fetchProfile = useCallback(() => {
    pipe(
      sequenceS(T.ApplyPar)({
        profile: AuthService.getProfile(),
      }),
      T.chainIOK(({ profile }) => () => {
        setProfile(O.fromEither(profile));

        setLoading(false);
      }),
    )();
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === AUTH_LOGOUT_KEY) {
        setProfile(O.none);
      }
    };

    window.addEventListener('storage', syncLogout, { passive: true });

    return () => {
      window.removeEventListener('storage', syncLogout);
      localStorage.removeItem(AUTH_LOGOUT_KEY);
    };
  }, [history]);

  const updateProfile = useCallback((profile: Profile) => setProfile(O.some(profile)), []);

  const logout = useCallback(() => {
    return pipe(
      AuthService.logout(),
      T.chainIOK(() => () => {
        localStorage.setItem(AUTH_LOGOUT_KEY, Date.now().toString());
        history.replace('/login', { ignorePrevent: true });
        setProfile(O.none);
      }),
    )();
  }, [history]);

  const ctx = useMemo<AuthContextValue>(
    () => ({
      profile,
      updateProfile,
      requestUpdateProfile: fetchProfile,
      logout,
    }),
    [profile, updateProfile, fetchProfile, logout],
  );

  return <AuthContext.Provider value={ctx}>{loading ? <DebouncedLineLoader /> : children}</AuthContext.Provider>;
};

export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}
